/**
 * Vault (files) WRITE hooks + the signed-URL mint — every one of them through
 * the Hono backend at `/api/v1/files`, never `supabase.from('files')`.
 *
 * THIS FILE MAY NEVER IMPORT `@/integrations/supabase/client`. The legacy
 * `FilesDataGrid` did all four of these straight from the browser (a
 * `.from('files').update()`, a `.from('files').delete()`, two
 * `storage.createSignedUrl()` calls); the ratchet allowlist in
 * `apps/web/eslint.supabase-import-ratchet.js` only ever SHRINKS, so the
 * replacement lands on `authedGet` / `authedPatch` / `authedDelete`.
 * See docs/BACKEND_API.md.
 *
 * ── THE HOOKS ARE SILENT ────────────────────────────────────────────────────
 * No toasts here, deliberately. The user-facing strings for this feature are
 * translated (`vault.*` in the three common.json files) and `t` belongs to the
 * component, so the CALLER raises the toast. `useFunds` puts hardcoded English
 * toasts in its hooks; that predates this feature's i18n requirement and is not
 * a pattern to copy into a translated surface.
 *
 * ── INVALIDATION ────────────────────────────────────────────────────────────
 * Each WRITE invalidates `['files', organizationId]` — the PREFIX of the index
 * key `['files', orgId, 'index', {}]` (`useVaultIndex`), and the same `'files'`
 * entity prefix `CreateFileDialog.tsx:153` (UploadFileDialog) already targets —
 * the sole external invalidation site since the Vault page became a leaf. The org comes
 * from the mutation VARIABLES, not from the response row: the variables are what
 * built the org-pinned URL the write went to, so the key can never drift from
 * the row that changed (and it still works if a response shape ever narrows).
 *
 * The signed-URL mint is the exception and invalidates NOTHING: it is a READ
 * (it mints a URL, it changes no row), so refetching the whole org index after
 * a "View file" click would be pure waste. It lives here rather than in
 * `useVaultIndex` because it is an imperative, click-driven fetch — a
 * `useMutation` gives the caller `isPending`/`variables` to gate its action bar
 * with, which `useQuery` would not.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authedDelete, authedGet, authedPatch } from '@/lib/api';
import type { UpdateFileInput } from '@releve/shared';

/**
 * The subset of the `public.files` row the write routes echo back that callers
 * actually read. The API returns the FULL row (`c.json({ file: data })`); this
 * is deliberately narrow so a column added to the table is not a change here.
 */
export interface VaultFileRow {
  id: string;
  organization_id: string | null;
  name: string;
  status: string;
}

/** `{ file }` — the envelope every write route returns. */
interface VaultFileEnvelope {
  file: VaultFileRow;
}

/**
 * Org-scoped path addressing ONE file row.
 *
 * `attachmentId` is the DELETE-only third argument, and it changes the verb's
 * meaning rather than its target: absent means "delete this file FROM THE
 * VAULT", present means "delete it VIA that attachment". Either way the server
 * counts the file's remaining references — excluding the named attachment when
 * one is given — and refuses with 409 if any survive.
 *
 * OMITTED, the result is byte-identical to the id-only form. It has to be:
 * `useFileSignedUrl` concatenates `/signed-url` onto this string, and an
 * unconditional query string would yield `…?attachment_id=x/signed-url` and
 * break View and Download.
 */
export const vaultFilePath = (
  organizationId: string,
  fileId: string,
  attachmentId?: string,
): string =>
  `/api/v1/files/orgs/${organizationId}/files/${fileId}` +
  (attachmentId ? `?attachment_id=${encodeURIComponent(attachmentId)}` : '');

/**
 * Org-scoped ATTACHMENT-create path — the single endpoint behind all four
 * attachment surfaces (activity / ledger transaction / AP invoice / asset).
 *
 * It lives here, next to `vaultFilePath`, because the three attachment hooks
 * (`useActivityAttachments`, `useTransactionAttachments`, `useAssetAttachments`)
 * POST to it and DELETE through `vaultFilePath` — one home for the
 * `/api/v1/files` URL shape rather than the string spelled out in four files.
 */
export const vaultAttachmentsPath = (organizationId: string): string =>
  `/api/v1/files/orgs/${organizationId}/attachments`;

/** Every write hook takes the org explicitly — it is half the URL. */
interface VaultFileTarget {
  organizationId: string;
  fileId: string;
}

export interface UpdateFileVariables extends VaultFileTarget {
  /**
   * Trimmed by the CALLER — the API's zod schema rejects an empty string.
   * ABSENT means "leave the name alone"; there is no way to clear it.
   */
  name?: string;
  /**
   * `null` CLEARS the project link; ABSENT leaves it untouched. The two are
   * genuinely different edits, and they stay different all the way down:
   * `updateFileSchema` is `.partial()` (key presence, not value) and
   * `public.update_vault_file` branches on the jsonb `?` key-exists operator.
   * Which is why this object is built by SPREADING only the keys the caller
   * passed — never by listing all three with `undefined` fillers, since
   * `JSON.stringify` would drop those and a `null` and an absent key would
   * become indistinguishable to the caller.
   */
  project_id?: string | null;
  /**
   * The whole tag set, replacing what is there. `[]` is a REAL edit (clear
   * every tag), never "absent".
   *
   * NOT a column: the RPC folds it into `metadata.tags` with `||`, so the
   * blob's sibling keys (the inbox pipeline's `source`, report generation's
   * own keys) survive. That merge is the entire reason the PATCH goes through
   * an RPC rather than a PostgREST `.update()`.
   */
  tags?: string[];
}

/**
 * Edit one file — any subset of name / project / tags, in one PATCH.
 *
 * Replaces the rename-only hook this file used to carry: the Vault's edit
 * dialog now covers all three fields, and `updateFileSchema` is still the
 * server-side field whitelist, so nothing else can ride along.
 *
 * `useSoftDeleteFile` below keeps its own hook rather than folding `status`
 * into this one — a soft delete is a different ACT (and a different
 * affordance) that happens to share a verb.
 */
export const useUpdateFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // `...patch` is the ONLY safe way to build the body: it carries exactly
    // the keys the caller set, preserving absent-vs-null (see the docblock).
    mutationFn: ({ organizationId, fileId, ...patch }: UpdateFileVariables) =>
      authedPatch<VaultFileEnvelope>(
        vaultFilePath(organizationId, fileId),
        patch satisfies UpdateFileInput,
      ),
    onSuccess: (_result, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
    },
  });
};

/**
 * SOFT delete — `status: 'deleted'`, the same write `RecentReports` does. The
 * row survives; `file_index` is scoped to `status = 'active'`, so the file
 * drops out of the Vault (and out of the copy-link route, which filters on the
 * same status) while its storage object and audit trail stay put.
 *
 * Kept alongside the hard delete because they are DIFFERENT acts, not two
 * spellings of one: this one is reversible and leaves the bytes; the other
 * destroys the row.
 */
export const useSoftDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, fileId }: VaultFileTarget) =>
      authedPatch<VaultFileEnvelope>(vaultFilePath(organizationId, fileId), {
        status: 'deleted',
      } satisfies UpdateFileInput),
    onSuccess: (_result, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
    },
  });
};

/**
 * HARD delete — removes the row, and best-effort its storage object.
 *
 * Storage removal SUCCEEDS as of `20260826120000`, which repaired the bucket's
 * DELETE policy — it previously compared `files.file_path` to `files.name`,
 * never referencing the object at all, so every removal failed. That is why the
 * legacy browser-side delete appeared to do nothing: it threw on the storage
 * call and never reached the row delete. The backend route still treats the
 * storage call as best-effort (a transient bucket error must not strand the
 * row), but the object is now genuinely removed.
 *
 * REFUSAL: this endpoint 409s when the file is attached to another record. A
 * file is deleted from the record it is attached to, never from the Vault — the
 * grid hides Delete on `is_attached` rows, and this is the authority behind
 * that affordance. Callers should branch on `ApiError.status === 409`.
 */
export const useDeleteFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, fileId }: VaultFileTarget) =>
      authedDelete<VaultFileEnvelope>(vaultFilePath(organizationId, fileId)),
    onSuccess: (_result, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
    },
  });
};

export interface FileSignedUrlVariables extends VaultFileTarget {
  /**
   * `true` → `?download=1`, which makes the server set Content-Disposition
   * from the ROW's name (an attachment download). Omit for an inline view.
   * The filename is never sent: the route reads it off the row it already
   * authorized.
   */
  download?: boolean;
}

/**
 * Mint a signed URL for one file (view inline, or download as an attachment).
 *
 * ID-KEYED, never path-keyed — the caller does not know, and must not send,
 * the storage path. That is why this replaces `useFileUrl`'s
 * `generateFileUrl(filePath)`: a browser-supplied path is an IDOR-shaped API
 * even when Storage RLS happens to catch it.
 *
 * A READ, so no invalidation (see the module docblock).
 */
export const useFileSignedUrl = () => {
  return useMutation({
    mutationFn: ({ organizationId, fileId, download }: FileSignedUrlVariables) =>
      authedGet<{ url: string }>(
        `${vaultFilePath(organizationId, fileId)}/signed-url${download ? '?download=1' : ''}`,
      ),
  });
};
